import { Form } from 'antd';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
  useGetAnalyticsWidgetMutation,
  useLazyGetAnalyticsFilterDataQuery,
} from '@app/api/Analytics';
import {
  today,
  WARNING,
  DATE_PICKER_FORMAT,
  FRONT_WARNING,
  ISO_FORMAT_INDEX,
  fistDayOfCurrentMonth,
} from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { useWidgetsConfig } from '@app/pages/Analytics/AnalyticsService/hooks/useWidgetsConfig';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import { WidthArchiveLabel } from '@app/shared/components';
import { useRegistryFilterState } from '@app/shared/hooks';
import type { NAnalytics } from '@app/types/Analytics';
import { addEnterKeyListener, handleDate } from '@app/utils';

import type { BaseOptionType } from 'antd/es/select';
import type { Dayjs } from 'dayjs';

type GetSelectOptionsData =
  | {
      label: string;
      value: string | number;
      filterName?: string | null;
      IsDeleted?: boolean;
    }[]
  | null;

export const useAnalytics = () => {
  const { activeBranchId } = useUserPrint();

  const [filterData, setFilterData] =
    useState<NAnalytics.ModelAnalyticsFilterData>();
  const [searchError, setSearchError] = useState('');
  // Выбранные фильтры отчёта переживают уход на другой раздел бокового меню и
  // возврат обратно (issue/11161): ключ поля включает имя отчёта, чтобы
  // отчёты аналитики не перетирали фильтры друг друга.
  const [selectedEmployees, setSelectedEmployees] = useRegistryFilterState<
    BaseOptionType[] | null
  >('analytics', 'services:selectedEmployees', null);
  const [selectedUsers, setSelectedUsers] = useRegistryFilterState<
    BaseOptionType[] | null
  >('analytics', 'services:selectedUsers', null);
  const [selectedClientServices, setSelectedClientServices] =
    useRegistryFilterState<BaseOptionType[] | null>(
      'analytics',
      'services:selectedClientServices',
      null
    );
  const [selectedClientServiceCategories, setSelectedClientServiceCategories] =
    useRegistryFilterState<BaseOptionType[] | null>(
      'analytics',
      'services:selectedClientServiceCategories',
      null
    );

  const { widgetConfig } = useWidgetsConfig();

  const [form] = Form.useForm();

  const { showNotification } = useContext(NotificationContext);

  const [getAnalyticsFilterData, { isFetching: isFiltersLoading }] =
    useLazyGetAnalyticsFilterDataQuery();
  const [getAnalyticsWidget] = useGetAnalyticsWidgetMutation();

  const getWidgetData = useCallback(
    async (
      WidgetId: NAnalytics.ModelAnalyticsFilter['WidgetId'],
      DateStart: Dayjs | string,
      DateEnd: Dayjs | string,
      setData: (data: NAnalytics.ModelAnalyticsWidget) => void,
      setIsWidgetLoading: (data: boolean) => void,
      isDefaultLoading: boolean
    ) => {
      try {
        setIsWidgetLoading(true);

        const handleIds = (options: BaseOptionType[] | null) =>
          isDefaultLoading ? null : options?.map(({ value }) => value) || null;

        const result = await getAnalyticsWidget({
          ActiveBranchId: activeBranchId,
          data: {
            WidgetId,
            DateStart: handleDate(DateStart),
            DateEnd: handleDate(DateEnd),
            EmployeeIds: handleIds(selectedEmployees),
            UserIds: handleIds(selectedUsers),
            ServiceIds: handleIds(selectedClientServices),
            CategoryIds: handleIds(selectedClientServiceCategories),
            PositionIds: null,
          },
        }).unwrap();

        setData(result);
      } catch (error) {
        showNotification(handleCatchError(error), WARNING);
      } finally {
        setIsWidgetLoading(false);
      }
    },
    [
      getAnalyticsWidget,
      activeBranchId,
      selectedEmployees,
      selectedUsers,
      selectedClientServices,
      selectedClientServiceCategories,
      showNotification,
    ]
  );

  const getWidgets = useCallback(
    async (
      DateStart: Dayjs | string,
      DateEnd: Dayjs | string,
      isDefaultLoading: boolean
    ) => {
      try {
        await Promise.all(
          widgetConfig.map(({ widgets }) => {
            widgets.map(({ widgetId, setData, setLoading }) =>
              getWidgetData(
                widgetId,
                DateStart,
                DateEnd,
                setData,
                setLoading,
                isDefaultLoading
              )
            );
          })
        );
      } catch (error) {
        showNotification(handleCatchError(error), WARNING);
      }
    },
    [getWidgetData, showNotification, widgetConfig]
  );

  useEffect(() => {
    const fetchAnalyticsFilterData = async () => {
      try {
        const result = await getAnalyticsFilterData({
          ActiveBranchId: activeBranchId,
        }).unwrap();

        const { ClientServices, ClientServiceCategories } = result;
        const sortedCategoryIds = new Set(
          ClientServices.map(({ CategoryId }) => CategoryId)
        );
        const sortedCategories: NAnalytics.ModelAnalyticsFilterData['ClientServiceCategories'] =
          [];

        sortedCategoryIds.forEach((categoryId) => {
          return ClientServiceCategories.forEach((category) => {
            if (category.Id === categoryId) {
              return sortedCategories.push(category);
            }
          });
        });

        setFilterData({
          ...result,
          ClientServiceCategories: sortedCategories,
        });
        // Первая загрузка учитывает фильтры, восстановленные из хранилища
        // (issue/11161): иначе фильтр на экране выбран, а цифры посчитаны
        // без него.
        getWidgets(fistDayOfCurrentMonth, today, false);
      } catch (error) {
        showNotification(handleCatchError(error), WARNING);
      }
    };

    fetchAnalyticsFilterData();
    // getWidgets вызывает некорректное поведение при фильтрации
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranchId, showNotification]);

  useEffect(() => {
    return addEnterKeyListener({
      callback: form.submit,
    });
  }, [form.submit]);

  const handleReset = () => {
    setSelectedEmployees([]);
    setSelectedUsers([]);
    setSelectedClientServices([]);
    setSelectedClientServiceCategories([]);
    form.setFieldValue('Date', [fistDayOfCurrentMonth, today]);

    getWidgets(fistDayOfCurrentMonth, today, true);
  };

  const submitForm = (values: { Date: [Dayjs, Dayjs] }) => {
    const hasEmptyDate = !values.Date || !values.Date[0] || !values.Date[1];

    if (hasEmptyDate)
      return showNotification(
        'Пожалуйста, заполните обе даты периода в фильтрах',
        FRONT_WARNING
      );

    const [startDate, endDate] = values.Date;
    const DateS = startDate.format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX]);
    const DateE = endDate.format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX]);

    getWidgets(DateS, DateE, false);
  };

  const getSelectOptions = useCallback((data: GetSelectOptionsData) => {
    if (!data?.length) return [];

    return data
      .sort((a, b) => {
        const isADeleted = a.IsDeleted || false;
        const isBDeleted = b.IsDeleted || false;

        if (isADeleted !== isBDeleted) {
          return isADeleted ? 1 : -1;
        }
        return a?.label?.localeCompare(b?.label ?? '') || 0;
      })
      .map(({ label, filterName, IsDeleted, value }) => ({
        label: <WidthArchiveLabel title={label} isDeleted={IsDeleted} />,
        value: value,
        filterName: filterName,
      }));
  }, []);

  const getEmployeesSelectOptions = useCallback(() => {
    if (!filterData?.Employees?.length) return [];
    const { Employees } = filterData;

    const data = Employees.map((employee) => {
      const label = `${employee?.NameLast} ${employee?.NameFirst}`?.trim();

      return {
        label,
        value: employee?.Id,
        filterName: label,
        IsDeleted: employee?.IsDeleted,
      };
    });

    return getSelectOptions(data);
  }, [filterData, getSelectOptions]);

  const getClientServiceSelectOptions = useCallback(() => {
    if (!filterData?.ClientServices) return [];

    const { ClientServices } = filterData;

    const data: GetSelectOptionsData = ClientServices.map((service) => {
      const label = (service?.Name || '')?.trim();
      return {
        label,
        value: service?.Id,
        filterName: label,
        IsDeleted: service?.IsDeleted,
      };
    });

    return getSelectOptions(data);
  }, [filterData, getSelectOptions]);

  const getClientServiceCategoriesSelectOptions = useCallback(() => {
    if (!filterData?.ClientServiceCategories) return [];

    const { ClientServiceCategories } = filterData;

    const data: GetSelectOptionsData = ClientServiceCategories.map(
      (category) => {
        const label = (category?.Name || '')?.trim();
        return {
          label,
          value: category?.Id,
          filterName: label,
          IsDeleted: category?.IsDeleted,
        };
      }
    );

    return getSelectOptions(data);
  }, [filterData, getSelectOptions]);

  const clientServiceCategoriesSelectOptions = useMemo(
    getClientServiceCategoriesSelectOptions,
    [getClientServiceCategoriesSelectOptions]
  );

  const clientServiceSelectOptions = useMemo(getClientServiceSelectOptions, [
    getClientServiceSelectOptions,
  ]);

  const employeesSelectOptions = useMemo(getEmployeesSelectOptions, [
    getEmployeesSelectOptions,
  ]);

  const userSelectOptions = useMemo(() => {
    if (!filterData?.Users?.length) return [];
    const { Users } = filterData;

    const data = Users.map((user) => {
      const label =
        [user?.NameLast, user?.NameFirst].filter(Boolean).join(' ').trim() ||
        'Без имени';
      return {
        label,
        value: user.Id,
        filterName: label,
        IsDeleted: user.IsDeleted,
      };
    });

    return getSelectOptions(data);
  }, [filterData, getSelectOptions]);

  return {
    isFiltersLoading,
    form,
    searchError,
    setSearchError,
    selectedEmployees,
    setSelectedEmployees,
    selectedUsers,
    setSelectedUsers,
    selectedClientServices,
    setSelectedClientServices,
    selectedClientServiceCategories,
    setSelectedClientServiceCategories,
    widgetConfig,
    handleReset,
    submitForm,
    employeesSelectOptions,
    clientServiceSelectOptions,
    clientServiceCategoriesSelectOptions,
    userSelectOptions,
  };
};
